import { TranslateService } from '@ngx-translate/core';
import { PipeTrackCell, PipeTrackStatus } from './pipe-track.component';
import { ResultStatus } from '../../models/test-run.model';
import { PipelineProgress, SegmentKind, runningSegmentIndex } from '../../models/live-pipeline.model';

const CELL_KEY = 'liveDashboard.livePipeline.track.cell';
const POSITION_KEY = 'liveDashboard.livePipeline.track.position';
const SUMMARY_KEY: Record<SegmentKind, string> = {
  TESTS: 'liveDashboard.livePipeline.track.summaryTests',
  JOBS: 'liveDashboard.livePipeline.track.summaryJobs',
};

/**
 * Die Farbe zu jedem Ergebnis, das das Backend meldet — vollzaehlig und ohne Sammelfall:
 * ein Sammelfall macht aus jedem Zustand, an den niemand gedacht hat, still einen offenen
 * Punkt. Bei GitHub Actions laufen mehrere Jobs zugleich, und uebersprungene zaehlt die
 * Karte daneben bereits als fertig — beides waere so falsch gezeichnet.
 *
 * <p>Uebersprungen und blockiert teilen sich die Farbe: beide sind fertig, ohne geprueft
 * worden zu sein. Das Wort dazu trennt sie ({@link STATUS_LABEL}).
 */
const CELL_STATUS: Record<ResultStatus, PipeTrackStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  RUNNING: 'running',
  SKIPPED: 'skipped',
  BLOCKED: 'skipped',
  NOT_RUN: 'pending',
};

const STATUS_LABEL: Record<ResultStatus, string> = {
  PASSED: 'liveDashboard.livePipeline.track.passed',
  FAILED: 'liveDashboard.livePipeline.track.failed',
  RUNNING: 'liveDashboard.livePipeline.track.running',
  SKIPPED: 'liveDashboard.livePipeline.track.skipped',
  BLOCKED: 'liveDashboard.livePipeline.track.blocked',
  NOT_RUN: 'liveDashboard.livePipeline.track.pending',
};

/**
 * Die Felder der Leiste aus den Punkten eines Durchlaufs (NEX-1232, NEX-1233, NEX-1292).
 *
 * <p>Gezeichnet wird, was das Backend liefert, statt aus Zaehlern zu rechnen: derselbe
 * Punkt steht bei einem ausgeloesten Durchlauf fuer einen Testfall und bei einem
 * beobachteten Lauf fuer einen Job (NEX-1290). Jedes Feld traegt deshalb seine eigene
 * Beschriftung, statt als blosse Farbe dazustehen.
 *
 * <p>Laufend ist jeder Punkt, den das Backend so meldet — bei einer Pipeline sind das
 * mehrere zugleich, weil Jobs ohne Vorbedingung gemeinsam starten. Der abgeleitete
 * laufende Punkt kommt nur hinzu, wenn das Backend selbst keinen nennt.
 *
 * <p>Eine Stelle fuer beide Leser: die Dashboard-Karte und das Live-Band der
 * Testlauf-Uebersicht zeigten sonst verschiedene Staende desselben Durchlaufs.
 */
export function buildPipeTrackCells(run: PipelineProgress, translate: TranslateService): PipeTrackCell[] {
  const runningIndex = runningSegmentIndex(run);
  const active = run.status === 'RUNNING';
  return run.segments.map((segment, index) => {
    const result = cellResult(segment.status, index === runningIndex, active);
    return {
      status: CELL_STATUS[result],
      label: translate.instant(CELL_KEY, {
        name: segment.label ?? translate.instant(POSITION_KEY, { index: index + 1 }),
        status: translate.instant(STATUS_LABEL[result]),
      }),
    };
  });
}

/**
 * Die Textalternative der Leiste: was die Farben zeigen, noch einmal in Zahlen. Der
 * Punkt heisst dabei beim Namen dessen, wofuer er steht — Testfall oder Job —, weil
 * dieselbe Leiste beides zeichnet.
 */
export function buildPipeTrackSummary(cells: PipeTrackCell[], kind: SegmentKind, translate: TranslateService): string {
  const count = (status: PipeTrackStatus): number => cells.filter((cell) => cell.status === status).length;
  return translate.instant(SUMMARY_KEY[kind], {
    total: cells.length,
    passed: count('passed'),
    failed: count('failed'),
    running: count('running'),
    skipped: count('skipped'),
    pending: count('pending'),
  });
}

/**
 * Das Ergebnis, das ein Feld traegt. Ein stehengebliebener Punkt arbeitet nicht mehr,
 * sobald der Durchlauf endet — sonst liefe die Leiste weiter, obwohl sie fertig ist.
 */
function cellResult(status: ResultStatus, derivedRunning: boolean, active: boolean): ResultStatus {
  if (derivedRunning) {
    return 'RUNNING';
  }
  return status === 'RUNNING' && !active ? 'NOT_RUN' : status;
}
